import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SectionsDrawerItem from './SectionsDrawerItem';
import DiagnosticLinkedKpis from './DiagnosticLinkedKpis';
import { IDrawerItem, IDrawerSection } from '../../Types/Drawer/ISectionsDrawerItem';
import styles from './SectionsDrawer.module.css';
import { ContextMenuItem } from '../../Types/ContextMenuItem';
import { isToday, isYesterday, isWithinInterval, subDays } from 'date-fns';
import type { KpiAreaApiType } from '../../API/KPI/AreaKpi';
import type { AreaKpiSummary } from '../../Types/KPI/IAreaKpiSummary';

/** Drawer id from questionnaire slice — KPI block is rendered directly under this row. */
const CREATE_IMPROVEMENT_SECTION_ID = 'create-improvement';

function groupSectionsByDate(sections: IDrawerSection[]) {
  const today: IDrawerSection[] = [];
  const yesterday: IDrawerSection[] = [];
  const previous7Days: IDrawerSection[] = [];
  const older: IDrawerSection[] = [];

  sections.forEach(section => {
    if (!section.date) {
      older.push(section); // fallback if no date
      return;
    }

    const date = new Date(section.date);

    if (isToday(date)) {
      today.push(section);
    } else if (isYesterday(date)) {
      yesterday.push(section);
    } else if (isWithinInterval(date, { start: subDays(new Date(), 7), end: subDays(new Date(), 2) })) {
      previous7Days.push(section);
    } else {
      older.push(section);
    }
  });

  return {
    Today: today,
    Yesterday: yesterday,
    'Previous 7 Days': previous7Days,
    Older: older,
  };
}

type SectionsDrawerProps = {
  sections: IDrawerSection[];
  basePath: string;
  activeSectionId: string | undefined;
  activeItemId: string | undefined;
  onClickHandler?: (item: IDrawerItem) => void;
  /** Called when user navigates (clicks a section or item link). Use to e.g. close mobile overlay. */
  onNavigate?: () => void;
  sectionsOnly?: boolean;
  toolbar?: React.ReactNode;
  contextMenuItems?: ContextMenuItem[];
  dated?: boolean;
  onCheckBoxChanged?: (item: IDrawerItem, checked: boolean) => void;
  /** When set, shows linked KPIs for this questionnaire area (Evaluate / Develop). */
  diagnosticKpi?: {
    areaId: string;
    areaType: KpiAreaApiType;
    refreshToken?: number;
    prefetchedSummary?: AreaKpiSummary;
    hasKpiEdit?: boolean;
    onOpenKpiSummary?: (keyResultId?: string) => void;
  };
};

const SectionsDrawer: React.FC<SectionsDrawerProps> = ({
  sections,
  basePath,
  activeSectionId,
  activeItemId,
  onClickHandler,
  onNavigate,
  sectionsOnly,
  toolbar,
  contextMenuItems,
  dated,
  onCheckBoxChanged,
  diagnosticKpi,
}) => {
  const grouped = groupSectionsByDate(sections);

  const expandableSectionIds = useMemo(
    () => sections.filter((s) => (s.items?.length ?? 0) > 0).map((s) => s.id),
    [sections],
  );

  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(() => new Set());

  const prevActiveSectionIdRef = useRef<string | undefined>(undefined);

  // Keep the section we navigated away from expanded when using multi-expand (Expand all).
  // It was never added to expandedSectionIds because Expand all skips the active section.
  useEffect(() => {
    const prev = prevActiveSectionIdRef.current;
    prevActiveSectionIdRef.current = activeSectionId;

    if (prev === undefined || prev === activeSectionId) {
      return;
    }

    setExpandedSectionIds((current) => {
      if (current.size === 0) {
        return current;
      }
      if (!expandableSectionIds.includes(prev)) {
        return current;
      }
      if (current.has(prev)) {
        return current;
      }
      const next = new Set(current);
      next.add(prev);
      return next;
    });
  }, [activeSectionId, expandableSectionIds]);

  // One real section (e.g. Develop): keep its criteria visible on Introduction / Additional info / Create objective routes.
  const isSectionCriteriaVisible = useCallback(
    (sectionId: string) => {
      if (expandableSectionIds.length === 1 && sectionId === expandableSectionIds[0]) {
        return true;
      }
      return sectionId === activeSectionId || expandedSectionIds.has(sectionId);
    },
    [activeSectionId, expandedSectionIds, expandableSectionIds],
  );

  /** Extra-expanded sections only (active section is always open and is not tracked here). */
  const canCollapseExtras = expandedSectionIds.size > 0;

  const canExpandMore = expandableSectionIds.some(
    (id) => id !== activeSectionId && !expandedSectionIds.has(id),
  );

  const toggleExpandCollapseAll = useCallback(() => {
    if (expandableSectionIds.length === 0) return;
    if (expandedSectionIds.size > 0) {
      setExpandedSectionIds(new Set());
    } else {
      setExpandedSectionIds(
        new Set(expandableSectionIds.filter((id) => id !== activeSectionId)),
      );
    }
  }, [expandableSectionIds, activeSectionId, expandedSectionIds]);

  const showExpandControls =
    !sectionsOnly &&
    expandableSectionIds.length > 1 &&
    (canCollapseExtras || canExpandMore);

  return (
    <div>
      {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
      {showExpandControls && (
        <div className={styles.sectionsControls}>
          <button
            type="button"
            className={styles.expandCollapseAll}
            onClick={toggleExpandCollapseAll}
          >
            {canCollapseExtras ? "Collapse all" : "Expand all"}
          </button>
        </div>
      )}
      {Object.entries(grouped).map(([label, group]) =>
        group.length > 0 ? (
          <div key={label}>
            {dated && <div className={styles.sectionLabel}>{label}</div>}
            {group.map(section => (
              <React.Fragment key={section.id}>
                <SectionsDrawerItem
                  section={section}
                  activeSection={section.id === activeSectionId}
                  criteriaExpanded={isSectionCriteriaVisible(section.id)}
                  activeItemId={activeItemId}
                  basePath={basePath}
                  onClickHandler={onClickHandler}
                  onNavigate={onNavigate}
                  sectionsOnly={sectionsOnly}
                  contextMenuItems={contextMenuItems}
                  onCheckBoxChanged={onCheckBoxChanged}
                />
                {diagnosticKpi?.areaId &&
                  section.id === CREATE_IMPROVEMENT_SECTION_ID && (
                    <DiagnosticLinkedKpis
                      areaId={diagnosticKpi.areaId}
                      areaType={diagnosticKpi.areaType}
                      refreshToken={diagnosticKpi.refreshToken}
                      prefetchedSummary={diagnosticKpi.prefetchedSummary}
                      hasKpiEdit={diagnosticKpi.hasKpiEdit}
                      onOpenKpiSummary={diagnosticKpi.onOpenKpiSummary}
                    />
                  )}
              </React.Fragment>
            ))}
          </div>
        ) : null
      )}
    </div>
  );
};

export default SectionsDrawer;
